import React, { Component, Fragment } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import map from 'lodash/map';
import findIndex from 'lodash/findIndex';
import Bem from 'bemHelper';
import { I18nHoc } from 'helpers/i18n';
import cacheTerms from 'helpers/cacheTerms';
import formatDate from 'helpers/formatDate';
import beautifyNumber from 'helpers/beautifyNumber';
import Dict from 'api/data-dict';
import preload from 'api/data-preload';
import DataProvider from 'api/data-provider';
import seoPreload, { seoDecorator } from 'api/seo';
import Form, { Input, PhoneInput } from 'containers/form';
import contentBlock, { blockPreloader } from 'containers/content-block';
import Button from 'components/button';
import Tooltip from 'components/tooltip';
import Row, { Col } from 'components/row';
import { SeoText } from 'components/layouts';
import Breadcrumbs from 'components/breadcrumbs';
import { OpenGraphImage } from 'components/image';
import Icon, { iconTypes } from 'components/icon';
import { ModalButton, Header, Content, Footer } from 'components/modal';

import linksDomik from 'src-sites/domik/links';
import blockConfig from 'src-sites/domik/blocks/config';

import { renderModalAskUsQuestion } from '../body/footer';

import translates from './tariff-plans-i18n.json';

import 'styles/domik/section-tariff-plans/_page-tariff-plans.scss';

const bemClasses = new Bem('pageTariffPlans');

const oneDay = 1000 * 60 * 60 * 24;

@preload({
  ...seoPreload({ entity: 'pageTariffPlans' }, []),
  ...blockPreloader({
    project: 'domik',
    entity: 'pageTariffPlans'
  }, blockConfig, []),
  lcProducts: [Dict.preload, { url: 'crm/lc-products' }],
  tariffProduct: [DataProvider.preload, {
    url: 'domik/tariff-plans/tariff-product',
    cache: cacheTerms.longInfoProvider
  }],
  tariffTree: [DataProvider.preload, {
    url: 'domik/tariff-plans/tree-list',
    query: {
      'order-by': 'weight'
    },
    queryJson: true,
    cache: cacheTerms.longInfoProvider
  }]
})
@seoDecorator({
  advancedVars: {
    bottomText: 'Текст в самом низу'
  }
})
@I18nHoc(translates)
export default class PageTariffPlans extends Component {
  static propTypes = {
    className: PropTypes.string,
    seoData: PropTypes.shape({})
  };

  constructor(...props) {
    super(...props);

    this.lcProductsOrder = [];

    this.state = {
      pricePeriod: 'oneMonth',
      monthsCount: 1,
      isRegionAndCT: false
    }
  }

  renderDescription(modalDescription, modalHeader = '') {
    if (!modalDescription) return null;

    const { i18n } = this;

    return (
      <ModalButton
        {...bemClasses('btn', 'moreVertical')}
        classNameModal="modalTariffDescription"
        buttonProps={{
          title: i18n('information'),
          iconType: iconTypes.moreVertical
        }}
        size="content"
      >
        <Header closeButton children={modalHeader || i18n('information')} />
        <Content hFull adaptiveHeight>
          <div>
            <span
              {...bemClasses('text')}
              dangerouslySetInnerHTML={{ __html: modalDescription }}
            />
          </div>
        </Content>
        <Footer children={<span />} />
      </ModalButton>
    );
  }

  renderTooltip(description) {
    if (!description) return null;

    return (
      <Tooltip
        {...bemClasses('tooltip')}
        // trigger="click"
        titleHtml={
          <div {...bemClasses('tooltipContent')}>
            <span
              {...bemClasses('text')}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        }
      >
        <Icon {...bemClasses('icon', 'info')} type={iconTypes.infoCircleBorder} />
      </Tooltip>
    );
  }

  renderProductHeader =()=> {
    const lcProducts = get(this, 'props.lcProducts.data');
    this.lcProductsOrder = [];

    return (
      <thead {...bemClasses('tableHead')}>
        <tr {...bemClasses('tableRow', 'thead')}>
          <th {...bemClasses('tableCol')} colSpan="2" />
          {map(lcProducts, (product, key) => {
            const productMain = get(product, 'addition_fields.main_product', false);

            if (!get(product, 'addition_fields.no_tariff')) {
              this.lcProductsOrder.push({ productId: product.id, productMain });

              return (
                <th {...bemClasses('tableCol', { product: true, productMain })} key={key}>
                  <span {...bemClasses('text')} children={product.name} />
                </th>
              );
            }
          })}
        </tr>
      </thead>
    );
  }

  renderProductItem =(productId, itemId)=> {
    const tariffProduct = get(this, 'props.tariffProduct.data');
    const index = findIndex(tariffProduct, prod => (prod.product_id === productId && prod.tariff_id === itemId));
    const included = get(tariffProduct, `[${index}].included`) ?
      <Icon {...bemClasses('icon', 'check')} type={iconTypes.check} /> :
      <Icon {...bemClasses('icon', 'minus')} type={iconTypes.minus} />;
    const description = get(tariffProduct, `[${index}].description`);

    return (
      <>
        {included}
        {!!description && this.renderTooltip(description)}
      </>
    );
  }

  renderTablePrice =(productId)=> {
    const { i18n } = this;
    const lcProducts = get(this, 'props.lcProducts.data');
    const index = findIndex(lcProducts, prod => (prod.id === productId));

    if (index < 0) return null;

    const { pricePeriod, isRegionAndCT, monthsCount } = this.state;
    const priceWithoutDiscount = get(lcProducts, `[${index}].addition_fields.price_per_month`);
    let priceText = '';
    let monthsDiscount = 0;
    let regionDiscount = 0;
    if (priceWithoutDiscount) {
      if (isRegionAndCT) {
        regionDiscount = get(lcProducts, `[${index}].addition_fields.discount_for_regions`, 0);
      }
      if (pricePeriod === 'threeMonths') {
        monthsDiscount = get(lcProducts, `[${index}].addition_fields.discount_per_3_months`, 0);
      }
      if (pricePeriod === 'threeMonths') {
        monthsDiscount = get(lcProducts, `[${index}].addition_fields.discount_per_3_months`, 0);
      }
      if (pricePeriod === 'sixMonths') {
        monthsDiscount = get(lcProducts, `[${index}].addition_fields.discount_per_6_months`, 0);
      }
      if (pricePeriod === 'twelveMonths') {
        monthsDiscount = get(lcProducts, `[${index}].addition_fields.discount_per_12_months`, 0);
      }
    }

    const totalDiscount = monthsDiscount + regionDiscount;
    const price = Math.round(priceWithoutDiscount - priceWithoutDiscount * totalDiscount / 100, 2) * monthsCount;
    priceText = price;

    if (!price) priceText = i18n('free');

    const orderData = {
      product_name: get(lcProducts, `[${index}].name`),
      price_per_month: priceWithoutDiscount,
      months_count: monthsCount,
      period_discount: monthsDiscount,
      region_discount: regionDiscount,
      total_price: price
    };

    return (
      <div {...bemClasses('price')}>
        <div {...bemClasses('label')}>
          <span {...bemClasses('text', 'label')} children={beautifyNumber(priceText)} />
          {!!price && !!totalDiscount &&
            <>
              <span {...bemClasses('text', 'discount')} children={`-${totalDiscount}%`} />
              <div {...bemClasses('text', 'total')} children={beautifyNumber(priceWithoutDiscount * monthsCount)} />
            </>
          }
        </div>
        <ModalButton
          {...bemClasses('btn', 'order')}
          ref={el => this.modal = el}
          classNameModal="modalTariffOrder"
          buttonProps={{
            title: i18n('want'),
            label: i18n('want')
          }}
          size="content"
        >
          <Header closeButton children="" />
          <Content hFull adaptiveHeight>
            <div>
              {this.renderOrderForm(orderData)}
            </div>
          </Content>
        </ModalButton>
      </div>
    );
  }

  onSubmit =()=> {
    if (this.modal) this.modal.closeModal();
  }

  renderOrderForm =(orderData)=> {
    const { i18n } = this;
    const { monthsCount, pricePeriod } = this.state;
    const tomorrow = new Date(Date.now() + oneDay).toISOString();
    const tomorrowDate = new Date(Date.now() + oneDay);
    const endPeriod = new Date(tomorrowDate.setMonth(tomorrowDate.getMonth() + monthsCount)).toISOString();
    const totalDiscountText = orderData.region_discount
      ? `${orderData.region_discount}% ${i18n('and')} ${orderData.period_discount}%`
      : `${orderData.period_discount || 0}%`
    const defaultData = {
      data: orderData
    }

    return (
      <Row {...bemClasses('row', 'order')} mpColumn>
        <Col {...bemClasses('col', 'info')} allPr={0}>
          <div {...bemClasses('text', 'topic')} children={i18n('orderHeader')} />
          <table {...bemClasses('table', 'modal info')}>
            <tbody {...bemClasses('tableBody')}>
              <tr {...bemClasses('tableRow')}>
                <td {...bemClasses('tableCol')}>
                  <span {...bemClasses('text', 'label')} children={i18n('packageText')} />
                </td>
                <td {...bemClasses('tableCol')}>
                  <span {...bemClasses('text')} children={orderData.product_name} />
                </td>
              </tr>
              <tr {...bemClasses('tableRow')}>
                <td {...bemClasses('tableCol')}>
                  <span {...bemClasses('text', 'label')} children={i18n('packageStart')} />
                </td>
                <td {...bemClasses('tableCol')}>
                  <span
                    {...bemClasses('text')}
                    children={formatDate({
                      date: get(tomorrow.split('T'), '[0]'),
                      format: 'dd.mm.yyyy'
                    })}
                  />
                </td>
              </tr>
              <tr {...bemClasses('tableRow')}>
                <td {...bemClasses('tableCol')}>
                  <span {...bemClasses('text', 'label')} children={i18n('packagePeriod')} />
                </td>
                <td {...bemClasses('tableCol')}>
                  <span
                    {...bemClasses('text')}
                    children={`${i18n(pricePeriod)} (до\u00a0${formatDate({
                      date: get(endPeriod.split('T'), '[0]'),
                      format: 'dd.mm.yyyy'
                    })})`}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <table {...bemClasses('table', 'modal cost')}>
            <tbody {...bemClasses('tableBody')}>
              <tr {...bemClasses('tableRow')}>
                <td {...bemClasses('tableCol')}>
                    <span
                      {...bemClasses('text', 'label')}
                      children={`${i18n('selectedPayment')}${i18n(pricePeriod)}`}
                    />
                </td>
                <td {...bemClasses('tableCol')}>
                  {!!orderData.period_discount &&
                    <span {...bemClasses('text')} children={`-${orderData.period_discount}%`} />
                  }
                </td>
              </tr>
              {!!orderData.region_discount &&
                <tr {...bemClasses('tableRow')}>
                  <td {...bemClasses('tableCol')}>
                    <span {...bemClasses('text', 'label')} children={i18n('regionDiscount')} />
                  </td>
                  <td {...bemClasses('tableCol')}>
                    <span {...bemClasses('text')} children={`-${orderData.region_discount}%`} />
                  </td>
                </tr>
              }
              <tr {...bemClasses('tableRow')}>
                <td {...bemClasses('tableCol')}>
                  <span {...bemClasses('text', 'label')} children={`${i18n('totalSum')}${totalDiscountText})`} />
                </td>
                <td {...bemClasses('tableCol')}>
                  <span
                    {...bemClasses('text', 'cost')}
                    children={`${beautifyNumber(orderData.total_price)}\u00a0грн`}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
        <Col {...bemClasses('col', 'form')} allPr={0}>
          <Form
            {...bemClasses('form')}
            controller="tariff-plans/tariff-form"
            showPopupValidationMessages
            resetAfterSubmit
            data={defaultData}
            defaultValues={defaultData}
            defaultSuccessHeader={i18n('successNotification')}
            // onErrorSubmit={this.onErrorSubmit}
            onSubmit={this.onSubmit}
          >
            <Input
              {...bemClasses('input')}
              dataBind="name"
            />
            <PhoneInput
              {...bemClasses('input', 'phone')}
              dataBind="phone"
              noElementBefore
            />
            <Input
              {...bemClasses('input')}
              dataBind="email"
            />
            <Input
              {...bemClasses('input')}
              dataBind="lc_name"
            />
            <Input
              {...bemClasses('input')}
              dataBind="company_name"
            />
            <Input
              {...bemClasses('input')}
              dataBind="client_url"
            />
            <Button
              {...bemClasses('btn', 'submit')}
              label={i18n('makeOrder')}
              title={i18n('makeOrder')}
              data-submit
            />
          </Form>
        </Col>
      </Row>
    )
  }

  setOneMonth =()=> {
    this.setState( { pricePeriod: 'oneMonth', monthsCount: 1 });
  }

  setThreeMonths =()=> {
    this.setState( { pricePeriod: 'threeMonths', monthsCount: 3 });
  }

  setSixMonths =()=> {
    this.setState( { pricePeriod: 'sixMonths', monthsCount: 6 });
  }

  setTwelveMonths =()=> {
    this.setState( { pricePeriod: 'twelveMonths', monthsCount: 12 });
  }

  setRegionAndCT =()=> {
    this.setState( { isRegionAndCT: !this.state.isRegionAndCT });
  }

  renderPeriodPriceButton =(period, periodFunc)=> {
    const { i18n } = this;
    const { pricePeriod } = this.state;

    return (
      <Button
        {...bemClasses('btn', { filters: true, active: pricePeriod === period })}
        label={i18n(`${period}Short`)}
        title={i18n(`${period}Short`)}
        onClick={periodFunc}
      />
    )
  }

  renderPriceButtons =()=> {
    const { i18n } = this;
    const { isRegionAndCT } = this.state;

    return (
      <>
        <div {...bemClasses('label')}>
          <span {...bemClasses('text', 'label')} children={i18n('priceHeader')} />
        </div>
        <div {...bemClasses('btns')}>
          {this.renderPeriodPriceButton('oneMonth', this.setOneMonth)}
          {this.renderPeriodPriceButton('threeMonths', this.setThreeMonths)}
          {this.renderPeriodPriceButton('sixMonths', this.setSixMonths)}
          {this.renderPeriodPriceButton('twelveMonths', this.setTwelveMonths)}
          <Button
            {...bemClasses('btn', { filters: true, active: isRegionAndCT === true })}
            label={i18n('regionAndCT')}
            title={i18n('regionAndCT')}
            onClick={this.setRegionAndCT}
          />
        </div>
      </>
    );
  }

  renderTariffTree =({ data = []})=> {
    if (!data || !data.length) return <div {...bemClasses('tableContainer')} />;

    // const { i18n } = this;
    const lcProducts = get(this, 'props.lcProducts.data');

    return (
      <div {...bemClasses('tableContainer')}>
        <table {...bemClasses('table', 'main')}>
          {this.renderProductHeader()}
          <tbody {...bemClasses('tableBody')}>
            {map(data, (section, sectionKey) => (
              <Fragment key={sectionKey}>
                {section.children && !!section.children.length &&
                  <tr {...bemClasses('tableRow', 'first')}>
                    <td {...bemClasses('tableCol')} colSpan="2">
                      <span {...bemClasses('text')} children={section.tariff_title} />
                    </td>
                    {map(this.lcProductsOrder, (product, productKey) => (
                      <td
                        {...bemClasses('tableCol', { product: true, productMain: product.productMain })}
                        key={`${productKey}+${product.productId}`}
                        children={this.renderTablePrice(product.productId)}
                      />
                    ))}
                  </tr>
                }
                {section.children && !!section.children.length && map(section.children, (subsection, subsectionKey) => (
                  <Fragment key={subsectionKey}>
                    {subsection.children &&
                    !!subsection.children.length &&
                    map(subsection.children, (item, itemKey) => (
                      <tr {...bemClasses('tableRow')} key={itemKey}>
                        {itemKey === 0 ?
                          <td {...bemClasses('tableCol')}>
                            <span {...bemClasses('text')} children={subsection.tariff_title} />
                          </td> :
                          <td {...bemClasses('tableCol')} />
                        }
                        <td {...bemClasses('tableCol')}>
                          <span {...bemClasses('text')} children={item.tariff_title} />
                          {this.renderDescription(item.description, item.tariff_title)}
                        </td>
                        {map(this.lcProductsOrder, (product, productKey) => (
                          <td
                            {...bemClasses('tableCol', { product: true, productMain: product.productMain })}
                            key={productKey}
                            children={this.renderProductItem(product.productId, item.id)}
                          />
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
            <tr {...bemClasses('tableRow', 'first last')}>
              <td
                {...bemClasses('tableCol')}
                colSpan="2"
                children={this.renderPriceButtons()}
              />
              {map(this.lcProductsOrder, (product, productKey) => (
                <td
                  {...bemClasses('tableCol', { product: true, productMain: product.productMain })}
                  key={`${productKey}+${product.productId}`}
                  children={this.renderTablePrice(product.productId)}
                />
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { i18n } = this;
    const { className, seoData = {}, tariffTree } = this.props;
    const topic = seoData && seoData.header ? seoData.header : i18n('tariffH1');
    const askButtonProps = {
      // iconType: iconTypes.comment,
      label: i18n('haveQuestionsBtnText'),
      title: i18n('haveQuestionsBtnText'),
      contentLeft: true,
      dataView: null
    };
    const seoText = get(seoData, 'bottomText');
    const dataBreadcrumbs = [
      {
        name: i18n('breadcrumbsMain'),
        builder: linksDomik.sectionDomik,
        builderProps: { title: i18n('breadcrumbsMain') }
      }, {
        name: topic,
        builder: linksDomik.sectionTariffPlans,
        builderProps: { title: topic }
      }
    ];

    return (
      <div {...bemClasses({ extra: className })}>
        <LazyHydrate ssrOnly>
          <OpenGraphImage />
        </LazyHydrate>
        <div {...bemClasses('title')}>
          <div {...bemClasses('text', 'h1')}><h1 children={topic} /></div>
        </div>
        <DataProvider
          url="domik/tariff-plans/tree-list"
          query={{
            'order-by': 'weight'
          }}
          preloaded={tariffTree}
          renderer={this.renderTariffTree}
        />
        {/*
        <div {...bemClasses('afterTable')}>
          <span
            {...bemClasses('text', 'afterTable')}
            children={i18n('textAfterTable')}
          />
        </div>
        */}
        <div {...bemClasses('blockHaveQuestions')}>
          <div
            {...bemClasses('text', 'topic')}
            children={i18n('haveQuestionsTopic')}
          />
          <div {...bemClasses('text')}>
            {i18n('haveQuestionsText')}
            {renderModalAskUsQuestion.call(this, askButtonProps, bemClasses)}
          </div>
        </div>
        <LazyHydrate whenVisible>
          {contentBlock(blockConfig, 'bottom1', this.props, { classNameWrapper: 'blockPTariffPlansBottom1', gtmProps: { 'place': 'Actions' } })}
          {contentBlock(blockConfig, 'bottom2', this.props, { classNameWrapper: 'blockPTariffPlansBottom2', gtmProps: { 'place': 'Actions' } })}
          {contentBlock(blockConfig, 'bottom3', this.props, { classNameWrapper: 'blockPTariffPlansBottom3', gtmProps: { 'place': 'Actions' } })}
        </LazyHydrate>
        <SeoText data={seoText} />
        <Breadcrumbs
          {...bemClasses('breadcrumbs')}
          data={dataBreadcrumbs}
        />
      </div>
    );
  }
}
